"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BossApi = void 0;
const axios_1 = __importDefault(require("axios"));
const aes_1 = require("./aes");
const apiMap_1 = require("./apiMap");
class BossApi {
    /**
     * BossApi 类的构造函数
     * @param {BossApiConfig} config - 配置对象
     * @param {EventChannel} config.channel - 可选的 EventChannel 实例，用于跨标签页通信
     * @param {boolean} [config.debugMode=false] - 是否启用调试模式
     * @param {any} [config.messageToast] - 可选的消息提示对象，用于显示消息
     */
    constructor({ channel, debugMode, messageToast, event }) {
        this.requestDebugMap = new Map();
        // 优先使用传入的debugMode，否则从localStorage读取，最后默认为false
        this.debugMode = this.getDebugModeFromStorage(debugMode);
        this.channel = channel;
        this.message = messageToast;
        this.event = event;
        this.service = axios_1.default.create({
            baseURL: BossApi.BASE_URL || '',
            timeout: BossApi.TIMEOUT,
            headers: {
                'Content-Type': 'application/json',
            },
            withCredentials: true,
        });
        // 修改请求拦截器，确保有 sessionId
        this.service.interceptors.request.use(async (config) => {
            var _a, _b;
            const url = config.url || '';
            const isMediaUpload = url.endsWith('media/upload/file') || url.endsWith('media/upload/resume');
            const isSSR = (_a = config === null || config === void 0 ? void 0 : config.metadata) === null || _a === void 0 ? void 0 : _a.isSSR;
            const noEncrypt = (_b = config === null || config === void 0 ? void 0 : config.metadata) === null || _b === void 0 ? void 0 : _b.noEncrypt;
            config.timeout = config.timeout || BossApi.TIMEOUT; // 媒体上传请求超时默认设置为60秒
            // 保存原始数据用于调试
            if (this.debugMode) {
                const requestId = `${Date.now()}-${Math.random()}`;
                this.requestDebugMap.set(requestId, {
                    originalData: JSON.parse(JSON.stringify(config.data || {})),
                    isMediaUpload,
                    timestamp: Date.now(),
                });
                // 在config中保存requestId用于后续查找
                config.headers.set('X-Debug-Request-Id', requestId);
            }
            if (isMediaUpload) {
                config.headers.set('Content-Type', 'multipart/form-data');
                config.timeout = config.timeout || 60000; // 媒体上传请求超时默认设置为60秒
            }
            if (!isSSR && !isMediaUpload && !noEncrypt && typeof window !== 'undefined') {
                let sessionId = localStorage.getItem('sessionId');
                if (!sessionId) {
                    await BossApi.initializeHandshake();
                    sessionId = localStorage.getItem('sessionId');
                }
                else {
                    config.headers.set('Session-Id', sessionId);
                }
                // 非媒体上传请求的处理
                config.data = config.data || {};
                // 对请求数据进行加密和UTF-8编码
                const encryptedData = this.encrypt(config.data);
                const utf8EncodedData = BossApi.NO_ENCRYPT
                    ? encryptedData
                    : new TextEncoder().encode(encryptedData !== null && encryptedData !== void 0 ? encryptedData : '');
                config.data = utf8EncodedData;
                if (!BossApi.NO_ENCRYPT) {
                    config.headers.set('Content-Type', 'text/plain');
                }
            }
            return config;
        });
        // 响应拦截器
        this.service.interceptors.response.use(async (response) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
            try {
                const isSSR = (_b = (_a = response.config) === null || _a === void 0 ? void 0 : _a.metadata) === null || _b === void 0 ? void 0 : _b.isSSR;
                const noEmit = (_d = (_c = response.config) === null || _c === void 0 ? void 0 : _c.metadata) === null || _d === void 0 ? void 0 : _d.noEmit;
                // 解密响应数据
                let targetData = response.data;
                if (!isSSR) {
                    if (typeof response.data === 'string' && BossApi.isHex(response.data)) {
                        try {
                            targetData = this.decrypt(response.data);
                        }
                        catch (_q) {
                            // 解密失败时，重新握手并重试请求
                            await BossApi.performHandshake();
                            return this.service(response.config);
                        }
                    }
                    else if (((_e = response.data) === null || _e === void 0 ? void 0 : _e.data) && BossApi.isHex(response.data.data)) {
                        try {
                            targetData = Object.assign(Object.assign({}, response.data), { data: this.decrypt(response.data.data) });
                        }
                        catch (_r) {
                            // 解密失败时，重新握手并重试请求
                            await BossApi.performHandshake();
                            return this.service(response.config);
                        }
                    }
                }
                if (this.debugMode && !isSSR) {
                    // 使用动态访问确保console调用不被移除
                    const globalConsole = (typeof window !== 'undefined' ? window.console : null) || console;
                    const log = globalConsole.log.bind(globalConsole);
                    const group = globalConsole.group.bind(globalConsole);
                    const groupEnd = globalConsole.groupEnd.bind(globalConsole);
                    group('🔍 BossApi Debug Info');
                    // 从Map中获取原始请求数据
                    const requestId = (_f = response.config.headers) === null || _f === void 0 ? void 0 : _f['X-Debug-Request-Id'];
                    const debugInfo = requestId ? this.requestDebugMap.get(requestId) : null;
                    log('📡 Request:', BossApi.NO_TRANSFORM
                        ? response.config.url
                        : (0, apiMap_1.reverseTransformRoute)(response.config.url.replace(BossApi.API_PREFIX, '')), (_g = response.config.method) === null || _g === void 0 ? void 0 : _g.toUpperCase(), response.status, response.statusText);
                    if (debugInfo) {
                        log('📤 Request Data:', debugInfo.originalData);
                        if (debugInfo.isMediaUpload) {
                            log('📎 Media Upload Request');
                        }
                        log('⏱️ Request Duration:', `${Date.now() - debugInfo.timestamp}ms`);
                        // 清理已使用的调试信息
                        this.requestDebugMap.delete(requestId);
                    }
                    else {
                        log('📤 Request Data: (Debug info not available)');
                    }
                    log('🔓 Response Data:', targetData);
                    groupEnd();
                }
                // 处理不同的响应状态码
                const noEmitUrl = noEmit || ((_h = response.config.url) === null || _h === void 0 ? void 0 : _h.includes('oltlfg'));
                if ((targetData === null || targetData === void 0 ? void 0 : targetData.code) && !noEmitUrl) {
                    // 发送对应频道消息
                    (_j = this.channel) === null || _j === void 0 ? void 0 : _j.toPostMessage({
                        channelType: 'updateLoginStatus',
                        channelPayload: {
                            code: targetData.code,
                            message: targetData.message,
                            data: targetData.data,
                        },
                    }, 'Immediate');
                }
                if (this.event && BossApi.EVENT_CODES.includes(targetData === null || targetData === void 0 ? void 0 : targetData.code)) {
                    (_k = this.event) === null || _k === void 0 ? void 0 : _k.emit('apiEvent', {
                        code: targetData.code,
                        message: targetData.message,
                        data: targetData.data,
                        path: response.config.url,
                    });
                }
                if (((_l = this.message) === null || _l === void 0 ? void 0 : _l.error) &&
                    (targetData === null || targetData === void 0 ? void 0 : targetData.code) !== 0 &&
                    ((_m = BossApi.messageMap) === null || _m === void 0 ? void 0 : _m[targetData.code])) {
                    // 检查是否禁用消息提示
                    const noMessage = (_p = (_o = response.config) === null || _o === void 0 ? void 0 : _o.metadata) === null || _p === void 0 ? void 0 : _p.noMessage;
                    if (!noMessage) {
                        this.message.error(BossApi.messageMap[targetData.code]);
                    }
                }
                return targetData;
            }
            catch (error) {
                throw error;
            }
        }, async (error) => {
            var _a, _b, _c, _d;
            const isSSR = (_c = (_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.config) === null || _b === void 0 ? void 0 : _b.metadata) === null || _c === void 0 ? void 0 : _c.isSSR;
            if (((_d = error.response) === null || _d === void 0 ? void 0 : _d.status) === 401 && !isSSR) {
                // 获取或初始化重试计数
                error.config.retryCount = error.config.retryCount || 0;
                // 如果重试次数超过最大限制（比如1次），则不再重试
                if (error.config.retryCount >= 1) {
                    return Promise.reject(error);
                }
                // 增加重试计数
                error.config.retryCount += 1;
                // 尝试重新握手并重试请求
                await BossApi.performHandshake();
                return this.service(error.config);
            }
            return Promise.reject(error);
        });
        if (typeof window !== 'undefined' && window.localStorage) {
            const sharedKey = localStorage.getItem('sharedKey');
            if (!sharedKey || !BossApi.isHex(sharedKey)) {
                BossApi.performHandshake();
            }
        }
        this.request = this.request.bind(this);
        this.post = this.post.bind(this);
        this.get = this.get.bind(this);
    }
    async request(url, options) {
        var _a, _b;
        try {
            const response = await this.service({
                url: BossApi.BASE_URL + ((options === null || options === void 0 ? void 0 : options.prefix) ? (options === null || options === void 0 ? void 0 : options.prefix) + url : BossApi.API_PREFIX + url),
                method: options.method,
                data: options.method !== 'GET' ? options.data : undefined,
                params: options.method === 'GET' ? options.data : undefined,
                headers: Object.assign({ 'Accept-Language': (_a = options === null || options === void 0 ? void 0 : options.lang) !== null && _a !== void 0 ? _a : BossApi.getLang(), "X-System-Version": `v2.3.1-301` }, options.headers),
                timeout: (_b = options.timeout) !== null && _b !== void 0 ? _b : BossApi.TIMEOUT,
                // 使用自定义属性传递 noMessage
                metadata: {
                    noMessage: options.noMessage,
                    isSSR: options.isSSR,
                    noEncrypt: options.noEncrypt,
                    noEmit: options.noEmit,
                },
            });
            return response;
        }
        catch (error) {
            console.error('API Request Failed:', error);
            throw error;
        }
    }
    async post(url, data, opts) {
        var _a, _b, _c;
        return this.service({
            url: (opts === null || opts === void 0 ? void 0 : opts.prefix)
                ? (opts === null || opts === void 0 ? void 0 : opts.prefix) + this.transformURL(url)
                : BossApi.API_PREFIX + this.transformURL(url),
            method: 'post',
            timeout: (_a = opts === null || opts === void 0 ? void 0 : opts.timeout) !== null && _a !== void 0 ? _a : BossApi.TIMEOUT,
            data,
            headers: Object.assign({ 'Accept-Language': (_b = opts === null || opts === void 0 ? void 0 : opts.lang) !== null && _b !== void 0 ? _b : BossApi.getLang(), "X-System-Version": `v2.3.1-301` }, ((_c = opts === null || opts === void 0 ? void 0 : opts.headers) !== null && _c !== void 0 ? _c : {})),
            metadata: {
                noMessage: opts === null || opts === void 0 ? void 0 : opts.noMessage,
                isSSR: opts === null || opts === void 0 ? void 0 : opts.isSSR,
                noEncrypt: opts === null || opts === void 0 ? void 0 : opts.noEncrypt,
                noEmit: opts === null || opts === void 0 ? void 0 : opts.noEmit,
            },
        });
    }
    async get(url, params, opts) {
        var _a, _b, _c;
        return this.service({
            url: (opts === null || opts === void 0 ? void 0 : opts.prefix)
                ? (opts === null || opts === void 0 ? void 0 : opts.prefix) + this.transformURL(url)
                : BossApi.API_PREFIX + this.transformURL(url),
            method: 'get',
            params,
            timeout: (_a = opts === null || opts === void 0 ? void 0 : opts.timeout) !== null && _a !== void 0 ? _a : BossApi.TIMEOUT,
            headers: Object.assign({ 'Accept-Language': (_b = opts === null || opts === void 0 ? void 0 : opts.lang) !== null && _b !== void 0 ? _b : BossApi.getLang(), "X-System-Version": `v2.3.1-301` }, ((_c = opts === null || opts === void 0 ? void 0 : opts.headers) !== null && _c !== void 0 ? _c : {})),
            // 使用自定义属性传递 noMessage
            metadata: {
                noMessage: opts === null || opts === void 0 ? void 0 : opts.noMessage,
                isSSR: opts === null || opts === void 0 ? void 0 : opts.isSSR,
                noEncrypt: opts === null || opts === void 0 ? void 0 : opts.noEncrypt,
                noEmit: opts === null || opts === void 0 ? void 0 : opts.noEmit,
            },
        });
    }
    static async handShake(data) {
        const url = BossApi.NO_TRANSFORM
            ? BossApi.BASE_URL + BossApi.API_PREFIX + '/v1/guest/handShake'
            : (0, apiMap_1.targetApi)(BossApi.BASE_URL + BossApi.API_PREFIX, `/v1/guest/handShake`);
        return (0, axios_1.default)(url, {
            method: 'POST',
            data,
            headers: {
                "X-System-Version": `v2.3.1-301`,
            }
        });
    }
    /**
     * 执行密钥交换的握手过程
     * 该方法会生成一对 X25519 密钥对，发送公钥到服务器，
     * 接收服务器的公钥和会话 ID，计算共享密钥，
     * 并将会话 ID 和共享密钥存储到 localStorage 中。
     * @returns {Promise<boolean>} 返回一个 Promise，表示握手是否成功
     */
    static async performHandshake() {
        try {
            // 添加锁定机制，防止并发握手
            if (typeof window !== 'undefined' && window._handshakeLock) {
                await window._handshakeLock;
                return true;
            }
            const handshakePromise = (async () => {
                var _a;
                const { privateKey, publicKey } = (0, aes_1.formatX25519KeyPair)();
                const response = await this.handShake({
                    clientPubKey: (0, aes_1.arrayBufferToHex)(publicKey),
                });
                if (((_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.code) === 0) {
                    const { serverPubKey, sessionId } = response.data.data;
                    if (typeof window !== 'undefined') {
                        localStorage.setItem('sessionId', sessionId);
                        const sharedKey = (0, aes_1.computeSharedKey)(privateKey, serverPubKey);
                        localStorage.setItem('sharedKey', (0, aes_1.arrayBufferToHex)(sharedKey));
                    }
                    return true;
                }
                return false;
            })();
            if (typeof window !== 'undefined') {
                window._handshakeLock = handshakePromise;
            }
            const result = await handshakePromise;
            if (typeof window !== 'undefined') {
                window._handshakeLock = null;
            }
            return result;
        }
        catch (error) {
            if (typeof window !== 'undefined') {
                window._handshakeLock = null;
            }
            console.error('密钥交换过程出错:', error);
            return false;
        }
    }
    /**
     * 检查字符串是否为十六进制格式
     * @param str
     * @returns
     */
    static isHex(str) {
        const hexRegex = /^[0-9a-fA-F]+$/;
        return typeof str === 'string' && hexRegex.test(str);
    }
    /**
     * 初始化握手
     * 检查 localStorage 中是否存在 sessionId 和 sharedKey，如果不存在则执行握手
     * 如果存在，则不执行握手，直接返回
     * 该方法用于确保在使用 API 前已经完成握手过程
     * @returns {Promise<void>}
     * @throws {Error} 如果握手失败，将抛出错误
     * @example
     * await BossApi.initializeHandshake();
     */
    static async initializeHandshake() {
        if (typeof window === 'undefined') {
            return;
        }
        const sessionId = localStorage.getItem('sessionId');
        const sharedKey = localStorage.getItem('sharedKey');
        if (!sessionId || !sharedKey) {
            await this.performHandshake();
        }
    }
    /**
     * @returns 当前语言代码
     * 根据 localStorage 中的 langCode 获取当前语言代码，默认为 'vi'
     * 如果 langCode 是 'zh'，则返回 'zh-cn'
     */
    static getLang() {
        if (BossApi.IS_SSR || typeof window === 'undefined') {
            return BossApi.LANG;
        }
        const storeLang = localStorage.getItem('langCode') || 'vi';
        if (storeLang === 'zh') {
            return 'zh-cn';
        }
        if (storeLang === 'vi') {
            return 'vi-vn';
        }
        if (storeLang === 'en') {
            return 'en';
        }
    }
    static setLang(lang) {
        BossApi.LANG = lang;
        if (BossApi.IS_SSR || typeof window === 'undefined') {
            // 服务器端渲染或无窗口环境下不操作 localStorage
        }
        else {
            localStorage.setItem('langCode', lang);
        }
    }
    /**
     * 从localStorage获取调试模式设置
     * @returns {boolean} 调试模式状态
     */
    getDebugModeFromStorage(mode = false) {
        if (mode === true) {
            return true;
        }
        if (typeof window === 'undefined' || !window.localStorage) {
            return false;
        }
        try {
            const debugMode = localStorage.getItem('bossApiDebugMode');
            return debugMode === 'true';
        }
        catch (error) {
            console.warn('读取调试模式设置失败:', error);
            return false;
        }
    }
    transformURL(url) {
        return BossApi.NO_TRANSFORM ? url : (0, apiMap_1.transformRoute)(url);
    }
    encrypt(data) {
        return BossApi.NO_ENCRYPT ? data : (0, aes_1.encrypted)(data);
    }
    decrypt(data) {
        return BossApi.NO_ENCRYPT ? data : (0, aes_1.decrypted)(data);
    }
    setEventChannel(channel) {
        this.channel = channel;
    }
}
exports.BossApi = BossApi;
BossApi.BASE_URL = 'https://bossvn.com';
BossApi.API_PREFIX = '/api';
BossApi.LANG = 'vi';
BossApi.IS_SSR = false;
BossApi.NO_TRANSFORM = false;
BossApi.NO_ENCRYPT = false;
BossApi.EVENT_CODES = [];
BossApi.TIMEOUT = 10000;
BossApi.messageMap = {};
